import { Briefcase, CaretDown, Check, PencilSimple } from "@phosphor-icons/react";
import { Checkbox } from "@common/index";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { useState } from "react";

export type BrandSelectOption = {
  id: string;
  name: string;
  logoUrl?: string | null;
  accent?: string;
  isDefault?: boolean;
  creatorName?: string;
};

type BrandSelectMenuProps = {
  value: string;
  selectedIds?: string[];
  multiple?: boolean;
  options: BrandSelectOption[];
  emptyLabel: string;
  emptyValue?: string;
  multipleSelectionLabel?: (count: number) => string;
  defaultOptionLabel?: string;
  ariaLabel: string;
  disabled?: boolean;
  onChange: (brandId: string) => void;
  onSelectedIdsChange?: (brandIds: string[]) => void;
  triggerClassName?: string;
  triggerVariant?: "select" | "edit";
  align?: "start" | "center" | "end";
};

const BrandOptionAvatar = ({ option }: { option: BrandSelectOption }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showLogo = Boolean(option.logoUrl) && !imageFailed;

  return (
    <span
      aria-hidden="true"
      className={clsx(
        "flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md",
        showLogo
          ? "text-white"
          : "border border-[#E2E6F2] bg-[#F4F5F8] text-[#1A162F] shadow-xs",
      )}
      style={showLogo ? { backgroundColor: option.accent } : undefined}
    >
      {showLogo ? (
        <img
          src={option.logoUrl || undefined}
          alt=""
          className="h-full w-full bg-white object-contain p-px"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <Briefcase size={14} weight="bold" />
      )}
    </span>
  );
};

const BrandSelectMenu = ({
  value,
  selectedIds = [],
  multiple = false,
  options,
  emptyLabel,
  emptyValue = "",
  multipleSelectionLabel,
  defaultOptionLabel,
  ariaLabel,
  disabled = false,
  onChange,
  onSelectedIdsChange,
  triggerClassName,
  triggerVariant = "select",
  align = "start",
}: BrandSelectMenuProps) => {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((option) => option.id === value);
  const selectedOptions = options.filter((option) =>
    selectedIds.includes(option.id),
  );
  const selectedLabel = multiple
    ? selectedOptions.length > 0
      ? multipleSelectionLabel?.(selectedOptions.length) ||
        `${selectedOptions.length} selected`
      : emptyLabel
    : selectedOption?.name || emptyLabel;
  const menuOptions = multiple
    ? options
    : [{ id: emptyValue, name: emptyLabel }, ...options];

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={clsx(
            triggerVariant === "edit"
              ? "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 outline-none transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] focus-visible:bg-[#F4F5F8] focus-visible:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
              : "flex w-full items-center justify-between gap-2 rounded-[9px] border border-[#C8D0E2] bg-white px-2.5 text-left text-xs font-medium text-[#3D394D] outline-none transition-colors hover:border-[#98A2C0] focus-visible:border-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400",
            triggerClassName,
          )}
        >
          {triggerVariant === "edit" ? (
            <PencilSimple size={16} />
          ) : (
            <>
              <span className="flex min-w-0 items-center gap-2.5">
                {!multiple && selectedOption ? (
                  <BrandOptionAvatar option={selectedOption} />
                ) : null}
                <span className="min-w-0 truncate">{selectedLabel}</span>
                {!multiple &&
                selectedOption?.isDefault &&
                defaultOptionLabel ? (
                  <span className="shrink-0 rounded-full bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-medium text-[#465A8C]">
                    {defaultOptionLabel}
                  </span>
                ) : null}
              </span>
              <CaretDown size={14} className="shrink-0 text-[#7C86A6]" />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          className="z-[10050] max-h-64 overflow-y-auto rounded-xl border border-[#E8EBF3] bg-white p-1.5 shadow-[0_12px_28px_rgba(23,29,56,0.16)]"
          style={{
            width: "var(--radix-dropdown-menu-trigger-width)",
            minWidth: "var(--radix-dropdown-menu-trigger-width)",
            maxWidth: "calc(100vw - 16px)",
          }}
        >
          {menuOptions.map((option) => {
            const isSelected = multiple
              ? selectedIds.includes(option.id)
              : option.id === value;

            return (
              <DropdownMenu.Item
                key={option.id || "empty-option"}
                onSelect={(event) => {
                  if (multiple) {
                    event.preventDefault();
                    onSelectedIdsChange?.(
                      isSelected
                        ? selectedIds.filter((brandId) => brandId !== option.id)
                        : [...selectedIds, option.id],
                    );
                    return;
                  }

                  onChange(option.id);
                  setOpen(false);
                }}
                className={clsx(
                  "flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-sm text-[#3D394D] outline-none transition-colors data-[highlighted]:bg-[#F4F5F8] data-[highlighted]:text-[#1A162F]",
                  isSelected && "bg-[#F4F5F8] text-[#1A162F]",
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  {multiple ? (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => undefined}
                      aria-hidden="true"
                      tabIndex={-1}
                      className="pointer-events-none"
                    />
                  ) : null}
                  {option.id ? <BrandOptionAvatar option={option} /> : null}
                  <span className="min-w-0 truncate">{option.name}</span>
                  {option.isDefault && defaultOptionLabel ? (
                    <span className="shrink-0 rounded-full bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-medium text-[#465A8C]">
                      {defaultOptionLabel}
                    </span>
                  ) : null}
                </span>
                {isSelected && !multiple ? (
                  <Check
                    size={16}
                    weight="bold"
                    className="shrink-0 text-[#1A162F]"
                  />
                ) : null}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default BrandSelectMenu;
